import { computed } from 'vue'
import { CHART_PALETTE } from '../lib/apexTheme.js'

const hashString = (value) => {
  let hash = 2166136261
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

const createRng = (seed) => {
  let state = seed >>> 0
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0
    return state / 4294967296
  }
}

export const createRandomWalk = (targetReturn, points, seedKey, options = {}) => {
  if (points <= 1) return [0]
  const rng = createRng(hashString(seedKey))
  const steps = points - 1
  const badDayBias = options.badDayBias ?? 0.4
  const touchNegative = options.touchNegative ?? false
  const drift = targetReturn / steps
  const baseVolatility = 0.35 + Math.abs(targetReturn) * 0.025
  const increments = []

  for (let i = 0; i < steps; i += 1) {
    const noise = (rng() - 0.5) * 2
    const volatilitySkew = 0.8 + rng() * 0.8
    const badDayChance = 0.22 + badDayBias * 0.22
    const isBadDay = rng() < badDayChance
    const badDayShock = isBadDay ? (0.3 + rng() * 1.05) : 0
    increments.push(drift + noise * baseVolatility * volatilitySkew - badDayShock)
  }

  const walk = [0]
  for (let i = 0; i < increments.length; i += 1) walk.push(walk[i] + increments[i])

  const correction = targetReturn - walk[walk.length - 1]
  let adjustedWalk = walk.map((value, i) => value + correction * (i / steps))

  if (touchNegative && !adjustedWalk.some((value) => value < 0)) {
    const dipIndex = Math.max(2, Math.floor(steps * (0.2 + rng() * 0.35)))
    const dipAmount = 0.9 + rng() * 1.4
    adjustedWalk = adjustedWalk.map((value, i) => {
      const distance = Math.abs(i - dipIndex)
      const influence = Math.max(0, 1 - distance / 4)
      return value - dipAmount * influence
    })
    const endCorrection = targetReturn - adjustedWalk[adjustedWalk.length - 1]
    adjustedWalk = adjustedWalk.map((value, i) => value + endCorrection * (i / steps))
  }

  return adjustedWalk.map((value) => Number(value.toFixed(2)))
}

const hexWithAlpha = (hex, alpha) => {
  const byte = Math.round(Math.max(0, Math.min(1, alpha)) * 255)
  return hex + byte.toString(16).padStart(2, '0').toUpperCase()
}

export function useTradingChart({
  rightPanelTab,
  chartStockEntries,
  topRankedEntries,
  tradingDates,
  isDark,
  activeSeriesName,
  isFocused,
}) {
  const dateLabels = computed(() =>
    tradingDates.value.map((_, i) => `TD ${i + 1}`)
  )

  const chartSeries = computed(() => {
    if (rightPanelTab.value === 'stocks') {
      return chartStockEntries.value.map((stock, index) => ({
        name: stock.symbol,
        data: createRandomWalk(
          stock.returnPct,
          tradingDates.value.length,
          `stock-${stock.symbol}-${index}`,
          {
            badDayBias: index < 3 ? 0.52 : 0.44,
            touchNegative: stock.returnPct < 0 || index === chartStockEntries.value.length - 1,
          }
        ),
      }))
    }

    return topRankedEntries.value.map((entry) => ({
      name: entry.display_name,
      data: createRandomWalk(
        entry.total_return,
        tradingDates.value.length,
        `${entry.display_name}-${entry.rank}`,
        {
          badDayBias: entry.rank <= 3 ? 0.55 : 0.42,
          touchNegative: entry.rank === 2 || entry.rank === 3 || entry.rank === 5,
        }
      ),
    }))
  })

  const seriesColors = computed(() => {
    const series = chartSeries.value
    const active = activeSeriesName.value
    return series.map((s, i) => {
      const base = CHART_PALETTE[i] ?? '#94a3b8'
      if (!active) return base
      if (s.name === active) return base
      return hexWithAlpha(base, 0.25)
    })
  })

  const chartStrokeWidths = computed(() => {
    const series = chartSeries.value
    const active = activeSeriesName.value
    if (!active) return series.map((_, i) => (i < 3 ? 3 : 2))
    return series.map((s, i) =>
      s.name === active ? 3.5 : isFocused.value ? 1 : i < 3 ? 2 : 1.5
    )
  })

  const chartOptions = computed(() => ({
    chart: {
      id: 'returns-chart',
      toolbar: { show: false },
      background: 'transparent',
      animations: { enabled: true, speed: 600 },
      parentHeightOffset: 0,
    },
    theme: { mode: isDark.value ? 'dark' : 'light' },
    colors: seriesColors.value,
    stroke: {
      curve: 'smooth',
      width: chartStrokeWidths.value,
    },
    xaxis: {
      categories: dateLabels.value,
      tickAmount: 6,
      title: {
        text: 'Trading Day Since Start',
        offsetY: -2,
        style: { fontSize: '11px' },
      },
      labels: {
        style: { fontSize: '11px' },
        offsetY: 0,
      },
    },
    yaxis: {
      title: { text: 'Return (%)' },
      labels: {
        formatter: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`,
        style: { fontSize: '11px' },
      },
    },
    tooltip: {
      x: {
        formatter: (_, { dataPointIndex }) =>
          tradingDates.value[dataPointIndex]?.toLocaleDateString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
          }) ?? '',
      },
      y: { formatter: (v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` },
    },
    legend: { show: false },
    grid: {
      borderColor: isDark.value ? '#1e3328' : '#ddf2e8',
      padding: { bottom: 0 },
    },
  }))

  return { chartSeries, chartOptions, dateLabels }
}
